const MB = 1024 * 1024;

export const formatFileSize = (bytes: number) => {
  const abs = Math.abs(bytes);
  if (abs >= MB) {
    return `${(abs / MB).toFixed(2)} MB`;
  }
  if (abs >= 1024) {
    return `${(abs / 1024).toFixed(2)} KB`;
  }
  return `${abs} B`;
};

export interface ResizeImageOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  maxBytes?: number;
}

const readFileAsDataUrl = (file: File): Promise<string> => (
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string') {
        resolve(result);
      } else {
        reject(new Error('Unsupported image data.'));
      }
    };
    reader.onerror = () => reject(reader.error ?? new Error('Unable to read file.'));
    reader.readAsDataURL(file);
  })
);

const loadImageElement = (src: string): Promise<HTMLImageElement> => (
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image.'));
    img.src = src;
  })
);

const canvasToBlob = (canvas: HTMLCanvasElement, mimeType: string, quality?: number): Promise<Blob> => (
  new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error('Unable to generate image blob.'));
      }
    }, mimeType, quality);
  })
);

const deriveFileName = (file: File, targetExtension: string) => {
  const base = file.name.replace(/\.[^.]+$/, '');
  return `${base || 'photo'}${targetExtension}`;
};

export async function resizeImageFile(
  file: File,
  {
    maxWidth = 2048,
    maxHeight = 2048,
    quality = 0.82,
    maxBytes = 2 * MB,
  }: ResizeImageOptions = {}
): Promise<File> {
  if (typeof window === 'undefined' || !file) {
    return file;
  }

  const dataUrl = await readFileAsDataUrl(file);
  const image = await loadImageElement(dataUrl);

  const { width: originalWidth, height: originalHeight } = image;
  const widthRatio = maxWidth / originalWidth;
  const heightRatio = maxHeight / originalHeight;
  const ratio = Math.min(1, widthRatio, heightRatio);

  const shouldDownscale = ratio < 1;
  const needsReencode = file.size > maxBytes;
  if (!shouldDownscale && !needsReencode) {
    // Already smaller than our targets
    return file;
  }

  const targetWidth = Math.max(1, Math.round(originalWidth * (shouldDownscale ? ratio : 1)));
  const targetHeight = Math.max(1, Math.round(originalHeight * (shouldDownscale ? ratio : 1)));

  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return file;
  }
  ctx.drawImage(image, 0, 0, targetWidth, targetHeight);

  const isPng = (file.type || '').toLowerCase().includes('png');
  const mimeType = isPng ? 'image/png' : 'image/jpeg';
  const preferredExtension = isPng ? '.png' : '.jpg';

  const clampQuality = (value: number) => Math.min(0.95, Math.max(0.45, value));
  let currentQuality = mimeType === 'image/jpeg' ? clampQuality(quality) : undefined;
  let blob = await canvasToBlob(canvas, mimeType, currentQuality);

  if (mimeType === 'image/jpeg' && maxBytes > 0) {
    while (blob.size > maxBytes && (currentQuality ?? 1) > 0.5) {
      currentQuality = clampQuality((currentQuality ?? 1) - 0.08);
      blob = await canvasToBlob(canvas, mimeType, currentQuality);
    }
  }

  if (blob.size >= file.size && !shouldDownscale) {
    // Re-encoding didn’t help, keep the original
    return file;
  }

  const optimizedName = preferredExtension && file.name.toLowerCase().endsWith(preferredExtension)
    ? file.name
    : deriveFileName(file, preferredExtension);

  return new File([blob], optimizedName, {
    type: mimeType,
    lastModified: Date.now(),
  });
}
